import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const UpdateContentItemTargetDate = graphql(`
  mutation UpdateContentItemTargetDate(
    $id: ContentItemId!
    $targetDate: DateTime
  ) {
    updateContentItem(input: { id: $id, targetDate: $targetDate }) {
      __typename
      ... on UpdateContentItemSuccess {
        contentItem {
          __typename
          id
          targetDate
        }
      }
      ... on UpdateContentItemFailure {
        message
      }
    }
  }
`)

type UpdateTargetDateResult =
  | { success: true }
  | { success: false; error: string }

/**
 * Set a channel-less draft's `targetDate` (drag-to-reschedule). An optimistic
 * write moves the card between calendar cells / the date queue immediately; the
 * refetch then re-evaluates the range- and presence-filtered connections
 * (calendar cells, No Date sidebar), which a normalized cache write can't do.
 */
export function useUpdateContentItemTargetDate(): (
  id: string,
  targetDate: string,
) => Promise<UpdateTargetDateResult> {
  const [mutate] = useMutation(UpdateContentItemTargetDate, {
    refetchQueries: [
      'GetCalendarContentItemDrafts',
      'GetContentItemList',
      'GetUndatedContentItemDrafts',
    ],
    // A sidebar→cell drop moves the draft between two filtered connections,
    // which only the refetch resolves (the optimistic write can't change
    // connection membership). Await it so the calendar holds the drag overlay
    // until the card lands in its cell, instead of snapping back to the sidebar
    // for a beat and then teleporting.
    awaitRefetchQueries: true,
  })

  return useCallback(
    async (id, targetDate) => {
      try {
        const { data } = await mutate({
          variables: { id, targetDate },
          optimisticResponse: {
            updateContentItem: {
              __typename: 'UpdateContentItemSuccess',
              contentItem: {
                __typename: 'ContentItem',
                id,
                targetDate,
              },
            },
          },
        })
        const payload = data?.updateContentItem
        if (payload?.__typename === 'UpdateContentItemSuccess') {
          return { success: true }
        }
        return {
          success: false,
          error:
            payload?.__typename === 'UpdateContentItemFailure'
              ? payload.message
              : 'Unknown error',
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      }
    },
    [mutate],
  )
}
